"use client";

import { useState } from "react";
import { ChevronDown, ChevronRight, Eye, EyeOff, Play, Check, Copy } from "lucide-react";

interface ApiParam {
  name: string;
  label: string;
  type: "string" | "number" | "boolean" | "select";
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

interface ApiDebuggerProps {
  apiUrl?: string;
  apiMethod?: string;
  apiParams?: ApiParam[];
}

export function ApiDebugger({ apiUrl, apiMethod = "POST", apiParams }: ApiDebuggerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [params, setParams] = useState<Record<string, string | number | boolean>>({});
  
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<{
    data: unknown;
    status: number;
    timeMs: number;
    error?: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // If no apiUrl or apiParams, don't render the component
  if (!apiUrl && (!apiParams || apiParams.length === 0)) {
    return null;
  }

  const handleParamChange = (name: string, value: string | number | boolean) => {
    setParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleCopy = () => {
    if (response?.data) {
      navigator.clipboard.writeText(JSON.stringify(response.data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiUrl) return;

    setIsLoading(true);
    setResponse(null);

    const startTime = performance.now();
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (apiKey) {
        headers["Authorization"] = `Bearer ${apiKey}`;
      }

      // Convert number/boolean types if necessary, though fetch handles most strings well
      const processedParams = { ...params };
      apiParams?.forEach(param => {
        if (param.type === 'number' && processedParams[param.name]) {
          processedParams[param.name] = Number(processedParams[param.name]);
        } else if (param.type === 'boolean' && processedParams[param.name]) {
          processedParams[param.name] = processedParams[param.name] === 'true' || processedParams[param.name] === true;
        }
      });

      const fetchOptions: RequestInit = {
        method: apiMethod.toUpperCase(),
        headers,
      };

      let finalUrl = apiUrl;
      if (apiMethod.toUpperCase() === "GET" || apiMethod.toUpperCase() === "HEAD") {
        const urlObj = new URL(finalUrl);
        Object.entries(processedParams).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            urlObj.searchParams.append(key, value.toString());
          }
        });
        finalUrl = urlObj.toString();
      } else {
        fetchOptions.body = JSON.stringify(processedParams);
      }

      const res = await fetch(finalUrl, fetchOptions);
      
      const endTime = performance.now();
      const timeMs = Math.round(endTime - startTime);
      
      let data;
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      } else {
        data = await res.text();
      }

      setResponse({
        data: res.ok ? data : null,
        status: res.status,
        timeMs,
        error: !res.ok ? (typeof data === 'string' ? data : JSON.stringify(data)) : undefined
      });
    } catch (err: unknown) {
      const endTime = performance.now();
      setResponse({
        data: null,
        status: 0,
        timeMs: Math.round(endTime - startTime),
        error: err instanceof Error ? err.message : "请求失败，可能是跨域问题或网络连接中断"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-12 mb-8 border border-[#e2e8f5] dark:border-[#1e293b] rounded-xl overflow-hidden shadow-sm">
      {/* Header / Collapsed Bar */}
      <div 
        role="button"
        tabIndex={0}
        className="flex items-center justify-between px-5 py-4 cursor-pointer bg-[#F5F7FA] dark:bg-[#0f172a] hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors border-l-4 border-l-[#335AFF]"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <span className="font-medium text-[#0f172a] dark:text-[#f8fafc]">在线调试</span>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex flex-col lg:flex-row bg-white dark:bg-transparent border-t border-[#e2e8f5] dark:border-[#1e293b]">
          
          {/* Left Column: Inputs (45%) */}
          <div className="w-full lg:w-[45%] p-6 lg:border-r border-[#e2e8f5] dark:border-[#1e293b]">
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-[#0f172a] dark:text-[#f8fafc] mb-1.5">
                  Authorization <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="请输入你的 API Key"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg pr-10 focus:outline-none focus:ring-2 focus:ring-[#335AFF]/20 focus:border-[#335AFF] dark:bg-gray-800/50 dark:text-white text-sm transition-shadow"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  API Key 可在<a href="https://b.yoo-ai.com/" target="_blank" rel="noopener noreferrer" className="text-[#335AFF] hover:underline mx-1">控制台</a>-创建应用后获取
                </p>
              </div>

              {/* Dynamic Parameters */}
              {apiParams?.map((param, idx) => (
                <div key={idx}>
                  <label className="block text-sm font-medium text-[#0f172a] dark:text-[#f8fafc] mb-1.5">
                    {param.label || param.name}
                    {param.required && <span className="text-red-500 ml-1">*</span>}
                    <span className="text-xs text-gray-400 ml-2 font-normal">({param.name})</span>
                  </label>
                  
                  {param.type === 'select' || param.type === 'boolean' ? (
                    <select
                      value={params[param.name] === undefined ? "" : params[param.name].toString()}
                      onChange={(e) => handleParamChange(param.name, param.type === 'boolean' ? (e.target.value === 'true') : e.target.value)}
                      required={param.required}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#335AFF]/20 focus:border-[#335AFF] dark:bg-gray-800/50 dark:text-white text-sm appearance-none"
                    >
                      <option value="" disabled>请选择 {param.label}</option>
                      {param.type === 'boolean' ? (
                        <>
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </>
                      ) : (
                        param.options?.map((opt, i) => (
                          <option key={i} value={opt}>
                            {opt}
                          </option>
                        ))
                      )}
                    </select>
                  ) : (
                    <input
                      type={param.type === 'number' ? 'number' : 'text'}
                      value={
                        typeof params[param.name] === "boolean"
                          ? ""
                          : (params[param.name] as string | number | undefined) ?? ""
                      }
                      onChange={(e) => handleParamChange(param.name, e.target.value)}
                      placeholder={param.placeholder || `请输入 ${param.label}`}
                      required={param.required}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#335AFF]/20 focus:border-[#335AFF] dark:bg-gray-800/50 dark:text-white text-sm"
                    />
                  )}
                </div>
              ))}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-6 bg-[#335AFF] hover:bg-blue-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>请求中...</span>
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 fill-current" />
                    <span>发送请求</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Right Column: Response (55%) */}
          <div className="w-full lg:w-[55%] p-6 bg-gray-50/50 dark:bg-transparent relative">
            
            {!response && !isLoading && (
              <div className="h-full min-h-[200px] flex items-center justify-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-xl">
                <span className="text-gray-400 dark:text-gray-500 text-sm">响应结果将在这里显示</span>
              </div>
            )}

            {isLoading && !response && (
              <div className="h-full min-h-[200px] flex items-center justify-center border border-gray-200 dark:border-gray-800 rounded-xl bg-white dark:bg-[#0f172a]">
                <span className="text-gray-400 dark:text-gray-500 text-sm animate-pulse">请求执行中...</span>
              </div>
            )}

            {response && (
              <div className="h-full flex flex-col rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f172a] shadow-sm">
                
                {/* Response Header */}
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Response</span>
                    <div className="flex items-center space-x-2 text-xs">
                      <span className={`px-2 py-0.5 rounded-full font-medium ${
                        response.status >= 200 && response.status < 300 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                          : response.status === 0
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {response.status === 0 ? 'Error' : response.status}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">{response.timeMs} ms</span>
                    </div>
                  </div>
                  
                  {response.data != null && (
                    <button 
                      onClick={handleCopy}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title="复制结果"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  )}
                </div>

                {/* Response Body */}
                <div className="p-4 overflow-auto max-h-[400px] text-sm">
                  {response.error ? (
                    <div className="text-red-500 dark:text-red-400 whitespace-pre-wrap font-mono">
                      {response.error}
                    </div>
                  ) : (
                    <pre className="text-gray-800 dark:text-gray-200 font-mono">
                      {JSON.stringify(response.data, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            )}
            
          </div>
        </div>
      )}
    </div>
  );
}
